// Direct browser -> LLM provider calls for the BYOK ("bring your own key")
// cloud mentor. The user's key is passed straight to Google's or OpenAI's
// public HTTPS API and nowhere else. Both endpoints send permissive CORS
// headers, so this works from the page with no proxy.
//
// `cloudLlm()` returns the same { request, generate, generateClassifyIntent }
// shape runEngineMentor expects, so the whole mentor pipeline (prompts,
// routers, finalize, RAG) is shared with the local-engine path.

export const CLOUD_MODELS = {
  gemini: "gemini-3.5-flash-lite",
  openai: "gpt-4o-mini",
};

export const CLOUD_PROVIDER_LABELS = {
  gemini: "Google Gemini",
  openai: "OpenAI",
};

// This module runs both from Chatbot.jsx (which has a real useT()) and from
// tests that call these functions directly with no `t` at all — same
// optional-`t`-with-Italian-default pattern as lib/sessionFile.js, so those
// direct-call tests keep asserting the Italian copy unchanged.
const DEFAULT_T = (key, vars) => {
  const dict = {
    "byok.errors.authInvalid": "chiave non valida, revocata o senza permessi",
    "byok.errors.rateLimited": "quota o rate limit del provider superati",
    "byok.errors.networkUnreachable": "rete non raggiungibile",
    "byok.errors.unknownError": "errore sconosciuto",
    "byok.errors.fetchFailed": "fetch fallita",
    "byok.errors.noContent": "nessun contenuto restituito",
    "byok.errors.unknownProvider": "provider sconosciuto: {provider}",
  };
  let s = dict[key] || key;
  if (vars) for (const k in vars) s = s.replace(`{${k}}`, vars[k]);
  return s;
};

// Normalised errors so the caller can decide "fall back to local" vs "tell the
// user their key is bad". Message prefix is always `BYOK_<KIND>: ...`.
function byokError(kind, detail) {
  return new Error(`BYOK_${kind}: ${detail}`);
}

/** Human blurb for a `BYOK_*` error message. */
export function friendlyByokError(message = "", t = DEFAULT_T) {
  if (message.startsWith("BYOK_AUTH")) return t("byok.errors.authInvalid");
  if (message.startsWith("BYOK_RATE")) return t("byok.errors.rateLimited");
  if (message.startsWith("BYOK_NETWORK")) return t("byok.errors.networkUnreachable");
  if (message.startsWith("BYOK_")) return message.replace(/^BYOK_[A-Z]+:\s*/, "");
  return message || t("byok.errors.unknownError");
}

// ---- Gemini -----------------------------------------------------------------

function toGeminiBody({ messages, temperature, maxTokens, json }) {
  const systemParts = [];
  const contents = [];
  for (const m of messages) {
    if (m.role === "system") {
      systemParts.push({ text: m.content });
      continue;
    }
    contents.push({
      role: m.role === "assistant" ? "model" : "user",
      parts: [{ text: m.content }],
    });
  }
  const body = {
    contents,
    generationConfig: {
      temperature,
      maxOutputTokens: maxTokens,
      ...(json ? { responseMimeType: "application/json" } : {}),
    },
  };
  if (systemParts.length) body.systemInstruction = { parts: systemParts };
  return body;
}

async function geminiChat({ apiKey, model = CLOUD_MODELS.gemini, messages, temperature, maxTokens, json, signal, t = DEFAULT_T }) {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/${encodeURIComponent(model)}:generateContent?key=${encodeURIComponent(apiKey)}`;
  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toGeminiBody({ messages, temperature, maxTokens, json })),
      signal,
    });
  } catch (e) {
    throw byokError("NETWORK", e?.message || t("byok.errors.fetchFailed"));
  }
  if (res.status === 400 || res.status === 403) {
    const detail = await safeErrText(res);
    if (/API_KEY_INVALID|API key not valid|PERMISSION_DENIED/i.test(detail)) throw byokError("AUTH", detail);
    throw byokError("HTTP", `${res.status} ${detail}`);
  }
  if (res.status === 429) throw byokError("RATE", await safeErrText(res));
  if (!res.ok) throw byokError("HTTP", `${res.status} ${await safeErrText(res)}`);

  const data = await res.json().catch(() => ({}));
  const cand = data?.candidates?.[0];
  const text = (cand?.content?.parts || []).map((p) => p.text || "").join("");
  if (!text) {
    // Safety block or empty candidate — surface it so the caller can fall back.
    throw byokError("EMPTY", cand?.finishReason || t("byok.errors.noContent"));
  }
  return text;
}

// ---- OpenAI ---------------------------------------------------------------

async function openaiChat({ apiKey, model = CLOUD_MODELS.openai, messages, temperature, maxTokens, json, signal, t = DEFAULT_T }) {
  let res;
  try {
    res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model,
        messages,
        temperature,
        max_tokens: maxTokens,
        ...(json ? { response_format: { type: "json_object" } } : {}),
      }),
      signal,
    });
  } catch (e) {
    throw byokError("NETWORK", e?.message || t("byok.errors.fetchFailed"));
  }
  if (res.status === 401) throw byokError("AUTH", await safeErrText(res));
  if (res.status === 429) throw byokError("RATE", await safeErrText(res));
  if (!res.ok) throw byokError("HTTP", `${res.status} ${await safeErrText(res)}`);

  const data = await res.json().catch(() => ({}));
  const text = data?.choices?.[0]?.message?.content || "";
  if (!text) throw byokError("EMPTY", t("byok.errors.noContent"));
  return text;
}

async function safeErrText(res) {
  try {
    const t = await res.text();
    return t.slice(0, 300);
  } catch (e) {
    return "";
  }
}

// ---- Model listing ----------------------------------------------------------
// Providers add and retire models often, so the BYOK picker asks the provider
// itself (with the user's key) instead of shipping a hardcoded list.

// Non-chat families that still advertise generateContent / sit in /v1/models.
const GEMINI_EXCLUDE = /tts|image|robotics|computer-use|transcribe|embedding|native-audio|live|banana/i;
const OPENAI_EXCLUDE = /audio|realtime|tts|transcribe|image|search|embedding|instruct|moderation|codex/i;

export function filterGeminiModels(models = []) {
  return models
    .filter((m) => (m.supportedGenerationMethods || []).includes("generateContent"))
    .map((m) => (m.name || "").replace(/^models\//, ""))
    .filter((id) => id.startsWith("gemini-") && !GEMINI_EXCLUDE.test(id))
    .sort();
}

export function filterOpenaiModels(models = []) {
  return models
    .map((m) => m.id || "")
    .filter((id) => /^(gpt-|o\d|chatgpt-)/.test(id) && !OPENAI_EXCLUDE.test(id))
    .sort();
}

/** Chat model ids available to this key, straight from the provider's API. */
export async function listCloudModels({ provider, apiKey, signal }) {
  const isGemini = provider === "gemini";
  const url = isGemini
    ? `https://generativelanguage.googleapis.com/v1beta/models?pageSize=1000&key=${encodeURIComponent(apiKey)}`
    : "https://api.openai.com/v1/models";
  let res;
  try {
    res = await fetch(url, isGemini ? { signal } : { headers: { Authorization: `Bearer ${apiKey}` }, signal });
  } catch (e) {
    throw byokError("NETWORK", e?.message || "fetch");
  }
  if (res.status === 401 || res.status === 403) throw byokError("AUTH", await safeErrText(res));
  if (!res.ok) throw byokError("HTTP", `${res.status} ${await safeErrText(res)}`);
  const data = await res.json().catch(() => ({}));
  return isGemini ? filterGeminiModels(data.models) : filterOpenaiModels(data.data);
}

// ---- Public: the injectable llm --------------------------------------------

export function cloudChat({ provider, apiKey, model, messages, temperature = 0.4, maxTokens = 1024, json = false, signal, t = DEFAULT_T }) {
  const m = model || CLOUD_MODELS[provider];
  if (provider === "gemini") return geminiChat({ apiKey, model: m, messages, temperature, maxTokens, json, signal, t });
  if (provider === "openai") return openaiChat({ apiKey, model: m, messages, temperature, maxTokens, json, signal, t });
  return Promise.reject(byokError("CONFIG", t("byok.errors.unknownProvider", { provider })));
}

/**
 * The `llm` object runEngineMentor accepts. `request` is the JSON-returning
 * mentor/brainstorm/summarize/scenario call; `generate` /
 * `generateClassifyIntent` are the short plain-text router classifications.
 * `t` (optional, from Chatbot.jsx's useT()) translates the handful of
 * error-detail strings this module authors itself (network/empty/config) —
 * defaults to Italian so direct-call tests are unaffected.
 */
export function cloudLlm({ provider, key, model, t = DEFAULT_T }) {
  const call = (messages, opts) => cloudChat({ provider, apiKey: key, model, messages, t, ...opts });
  return {
    async request({ messages, temperature = 0.4, max_tokens = 1024 }) {
      // Every runEngineMentor `request` call parses the result as JSON.
      const content = await call(messages, { temperature, maxTokens: max_tokens, json: true });
      return { content };
    },
    generate: (prompt) =>
      call([{ role: "user", content: prompt }], { temperature: 0.2, maxTokens: 24, json: false }),
    generateClassifyIntent: (prompt) =>
      call([{ role: "user", content: prompt }], { temperature: 0.2, maxTokens: 200, json: false }),
  };
}
